import React from 'react';
import { Link } from 'react-router-dom';
import { Car } from 'lucide-react';
import { usePageSeo } from '../hooks/usePageSeo';
import AutomobileBrandsTable from '../components/automobile/AutomobileBrandsTable';
import { useAutomobileCatalogData } from '../hooks/useAutomobileCatalogData';

interface Props {
  darkMode: boolean;
}

/** Otomobil marka kataloğu */
const AutomobilePage: React.FC<Props> = ({ darkMode }) => {
  const { catalog, loading, error, refresh } = useAutomobileCatalogData();
  const { brandCount, modelCount, verifiedBrandCount, defunctBrandCount } = catalog;
  const isLoading = loading && brandCount === 0;

  usePageSeo({
    title: 'Otomobil',
    description: `${modelCount > 0 ? modelCount.toLocaleString('tr-TR') : ''} model ve ${brandCount} marka — otomobil marka ve model kataloğu.`,
    keywords: 'otomobil, araba, marka, model, katalog',
    noindex: true,
  });

  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm';
  const text = darkMode ? 'text-gray-300' : 'text-gray-600';
  const heading = darkMode ? 'text-white' : 'text-gray-900';

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <header className={`rounded-2xl border p-6 sm:p-8 mb-6 ${card}`}>
          <div className="flex items-center gap-3 mb-4">
            <Car className="text-indigo-500" size={36} aria-hidden />
            <h1 className={`text-2xl sm:text-3xl font-bold ${heading}`}>Otomobil markaları</h1>
          </div>
          <p className={`text-sm leading-relaxed ${text}`}>
            {isLoading
              ? 'Yükleniyor…'
              : modelCount > 0
                ? `${brandCount} marka · ${modelCount} model · gövde tipine göre kategorize edilmiş tam katalog`
                : 'Katalog şu an yüklenemedi. Lütfen daha sonra tekrar deneyin.'}
          </p>
          {!isLoading && modelCount === 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void refresh()}
                className="text-sm px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
              >
                Tekrar dene
              </button>
              {error && (
                <span className={`text-xs ${text}`} title={error}>
                  ({error.length > 80 ? `${error.slice(0, 80)}…` : error})
                </span>
              )}
            </div>
          )}
          {(verifiedBrandCount != null || defunctBrandCount != null) && (
            <p className={`text-xs mt-2 ${text}`}>
              {verifiedBrandCount ?? 0} doğrulanmış şirket kaydı · {defunctBrandCount ?? 0} kapalı/birleşmiş marka
            </p>
          )}
        </header>

        <AutomobileBrandsTable darkMode={darkMode} />
      </div>
    </div>
  );
};

export default AutomobilePage;
